<template>
  <div class="builder-container" ref="container" v-shortkey="shortkeys" @shortkey="triggerShortkey">
    <div class="d-flex align-items-center">
      <select-input
        class="col"
        label="Result Macro"
        accessKey="M"
        ref="resultMacro"
        name="targetMacro"
        @initialized="handleLoadSelect"
        searchExpr="displayName"
        v-model="cytMacroId"
        :dataSource="cytologyMacroSource"
      ></select-input>
      <button class="col-1 btn btn-secondary mt-3" @click="handleClear">Clear</button>
    </div>
    <div @keydown="handleBuilderKeydown" class="row flex-wrap">
      <div class="col">
        <h3><u>T</u>ypes</h3>
        <button
          v-for="(item, idx) in mainTypes"
          :key="item.id"
          :data-id="item.id"
          :data-idx="idx"
          data-box="types"
          ref="types"
          @click="handleSelectType"
          class="btn d-flex align-items-center cursor-pointer w-100"
          :class="{
            'btn-selected': selectedTypeKey.includes(item.id),
            'btn-light': !selectedTypeKey.includes(item.id)
          }"
        >
          <h3 class="name">{{ item.name }}</h3>
          <IconButton
            v-if="hasSelectionType(item)"
            icon="plus"
            @click.stop="handleComment(item)"
            class="btn btn-link d-inline-flex p-1 ml-auto justify-between align-items-center ml-2"
          >
            <span class="ml-2"> Text </span>
          </IconButton>
        </button>
      </div>
      <div class="col" data-box="codes">
        <div class="d-flex align-items-center py-2">
          <h3>Codes</h3>
        </div>
        <DxList
          :show-selection-controls="true"
          :data-source="codesSource"
          :focus-state-enabled="true"
          :selected-item-keys="selectedCodeKeys"
          class="list"
          data-box="codes"
          ref="codes"
          :selection-mode="selectionMode"
          @selection-changed="listSelectionChanged"
        >
          <template #item="{ data }">
            <div class="list-item-wrapper" :title="data.name || data" v-tooltip="data.name || data">
              <span class="list-item-text">{{ data.name || data }}</span>
            </div>
          </template>
        </DxList>
      </div>
      <div class="col" data-box="modifiers" v-if="hasModifiers">
        <h3>Modifiers</h3>
        <div>
          <DxList
            :show-selection-controls="true"
            :selected-item-keys="selectedModifierKeys"
            class="list"
            data-box="modifiers"
            :focus-state-enabled="true"
            ref="modifiers"
            :selection-mode="selectionMode"
            :noDataText="selectedCodes ? 'No Modifiers' : 'Select a code to see modifiers'"
            :dataSource="modifiersSource"
            @selection-changed="modifierSelectionChanged"
          >
            <template #item="{ data }">
              <div
                class="list-item-wrapper"
                :title="data.name || data"
                v-tooltip="data.name || data"
              >
                <span class="list-item-text">{{ data.name || data }}</span>
              </div>
            </template>
          </DxList>
        </div>
      </div>
    </div>
    <transition name="fade-in">
      <div class="col" ref="preview" v-if="selectedCodesAndMods">
        <CytologyTextPreview :selectedCodes="selectedCodesAndMods" />
      </div>
    </transition>

    <Modal :status="isCommenterOpen" @close="isCommenterOpen = false">
      <form @submit.prevent="submitComment" class="d-flex flex-column p-4">
        <GeneralMacroEnabledEditor
          v-model="comment"
          :label="commentLabel"
          name="Comment"
          autoFocus
          isExpanded
        />
        <button class="btn btn-primary ml-auto mt-8">Save</button>
      </form>
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import SelectInput from "../common/SelectInput.vue";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import { DxList } from "devextreme-vue";
import DataSource from "devextreme/data/data_source";
import { CytCodeGroupEnum, CytologyModifierMap, CytTypeEnum } from "@/modules/enums";
import {
  dateRangeFilter,
  getAltKeys,
  getScrollPosition,
  getTextFromHtml,
  scrollToElement,
  scrollToPosition
} from "@/modules/helpers";
import CytologyTextPreview from "../CytologyTextPreview.vue";
import Modal from "../common/Modal.vue";
import GeneralMacroEnabledEditor from "../common/GeneralMacroEnabledEditor.vue";
import IconButton from "../common/IconButton.vue";
import { debounceTime, switchMap } from "rxjs/operators";

export default {
  name: "CytologyResultsBuilder",
  props: {
    specimenId: {
      type: Number
    }
  },
  components: {
    SelectInput,
    IconButton,
    DxList,
    Modal,
    CytologyTextPreview,
    GeneralMacroEnabledEditor
  },
  data() {
    return {
      shortkeys: {
        ...getAltKeys("mt")
      },
      commentLabel: "Comment",
      lastScrollPos: null,
      selectedTypeKey: [],
      commentTargetId: null,
      comment: "",
      isCommenterOpen: false,
      isLoading: false,
      result: {
        cytDiagnosticMacroId: null,
        macroIsModified: false,
        diagCodes: []
      },
      codesComponent: null,
      modifiersComponent: null,
      cytMacroId: null,
      selectedTypes: [],
      selectionMode: "multiple",
      selectedCodes: [],
      selectedModifiers: [],
      columns: [
        {
          dataField: "name",
          caption: "Name"
        },
        {
          dataField: "formattedText",
          caption: "Text",
          calculateCellValue: data => data.formattedText && getTextFromHtml(data.formattedText),
          minWidth: "250px",
          width: "40%",
          cssClass: "truncate"
        }
      ]
    };
  },
  domStreams: ["$changedState"],
  subscriptions() {
    const $debounceAndCancel = this.$changedState.pipe(
      debounceTime(500),
      switchMap(val => this.saveCytologyResults(val))
    );
    return {
      $debounceAndCancel
    };
  },
  mounted() {
    this.$emit("loaded", this);
  },
  methods: {
    handleLoadSelect(data) {
      const { component } = data;
      component.option("dropDownOptions", {
        height: "20px",
        showTitle: false,
        position: "bottom"
      });
    },
    handleLoadCodes(event) {
      this.codesComponent = event.component;
    },
    handleLoadModifiers(event) {
      this.modifiersComponent = event.component;
    },
    triggerShortkey(event) {
      const { srcKey } = event;

      if (srcKey === "m") {
        this.focusMacro();
      }
      if (srcKey === "t") {
        const types = this.$refs.types;
        if (types) {
          const first = types[0];
          first.focus();
          this.$nextTick(first.click());
        }
      }
    },
    handleBuilderKeydown(event) {
      const { code, key } = event;
      const srcKeyMap = {
        ArrowDown: "down",
        ArrowUp: "up",
        ArrowRight: "right",
        ArrowLeft: "left"
      };
      const srcKey = srcKeyMap[code || key];
      const codes = this.$refs.codes;
      const modifiers = this.$refs.modifiers;
      const activeEl = document.activeElement;
      let { box, idx, id } = activeEl.dataset;

      if (!box) {
        const activeList = activeEl.closest(".col");
        box = activeList?.dataset.box;
      }

      if (srcKey === "down") {
        if (idx && id && box === "types") {
          const nextIdx = parseInt(idx) + 1;
          if (nextIdx >= this.$refs.types.length) {
            this.$refs.types[0].focus();
            this.$nextTick(this.$refs.types[0].click());
          } else {
            const nextEl = this.$refs.types[nextIdx];
            if (nextEl) {
              nextEl.focus();
              this.$nextTick(nextEl.click());
            }
          }
        }

        if (box === "codes") {
          const { instance } = codes;
          const activeItem = instance.option("focusedElement");
          if (activeItem) {
            const nextItem = activeItem.nextElementSibling;
            if (nextItem) {
              instance.option("focusedElement", nextItem);
            }
          }
        }
        if (box === "modifiers") {
          const { instance } = modifiers;
          const activeItem = instance.option("focusedElement");
          if (activeItem) {
            const nextItem = activeItem.nextElementSibling;
            if (nextItem) {
              instance.option("focusedElement", nextItem);
            }
          }
        }
      }
      if (srcKey === "up") {
        if (idx && id && box === "types") {
          const nextIdx = parseInt(idx) - 1;
          if (nextIdx < 0) {
            const lastEl = this.$refs.types[this.$refs.types.length - 1];
            if (lastEl) {
              lastEl.focus();
              this.$nextTick(lastEl.click());
            }
          } else {
            const nextEl = this.$refs.types[nextIdx];
            if (nextEl) {
              nextEl.focus();
              this.$nextTick(nextEl.click());
            }
          }
        }
        if (box === "codes") {
          const { instance } = codes;
          const activeItem = instance.option("focusedElement");
          if (activeItem) {
            const nextItem = activeItem.previousElementSibling;
            if (nextItem) {
              instance.option("focusedElement", nextItem);
            }
          }
        }
        if (box === "modifiers") {
          const { instance } = modifiers;
          const activeItem = instance.option("focusedElement");
          if (activeItem) {
            const nextItem = activeItem.previousElementSibling;
            if (nextItem) {
              instance.option("focusedElement", nextItem);
            }
          }
        }
      }
      if (srcKey === "right") {
        if (box === "types") {
          if (codes) {
            const { instance } = codes;
            if (instance) {
              instance.focus();
            }
          }
        }
        if (box === "codes") {
          if (modifiers) {
            const instance = modifiers.instance;
            if (instance) {
              instance.focus();
            }
          }
        }
      }
      if (srcKey === "left") {
        if (box === "codes") {
          const types = this.$refs.types;
          if (types) {
            types[0].focus();
            this.$nextTick(types[0].click());
          }
        }
        if (box === "modifiers") {
          if (codes) {
            const { instance } = codes;
            instance.focus();
          }
        }
      }
    },
    handleFocusTypes() {
      this.$refs.container.focus();
    },
    focusMacro() {
      this.$refs.resultMacro.focus();
    },
    async handleLoadPrefill(startingValues) {
      if (!startingValues?.length) {
        this.selectedCodes = [];
        this.selectedModifiers = [];
      } else {
        for (const item of startingValues) {
          const diagCode = await cytologyService.getSingleEntity(
            CytologyEntities.CytDiagnosticCodes,
            item.cytDiagnosticCodeId
          );
          const groupId = diagCode?.cytCodeGroupId;
          if (this.topTypes.includes(groupId)) {
            this.selectedCodes.push({
              ...diagCode,
              comments: ""
            });
          } else {
            this.selectedModifiers.push({
              ...diagCode
            });
          }
        }
      }
      // Select the first type if no type is selected
      if (!this.selectedTypeKey?.length && this.mainTypes?.length) {
        const firstType = this.mainTypes[0];
        if (firstType?.id) {
          this.selectedTypeKey = [firstType.id];
        }
      }
    },
    handleClear() {
      this.selectedTypeKey = [];
      this.selectedTypes = [];
      this.selectedCodes = [];
      this.selectedModifiers = [];
      this.cytMacroId = null;
    },
    hasSelection() {
      return this.selectedCodes.length > 0;
    },
    handleSelectType(event) {
      const { id } = event.currentTarget.dataset;
      this.selectedTypeKey = [id];
    },
    listSelectionChanged(e) {
      const { addedItems, removedItems } = e;
      const removedKeys = removedItems.map(e => e.id);
      let currentItems = [...this.selectedCodes];
      const currentItemKeys = new Set(currentItems.map(e => e.id));
      let hasModified = false;
      for (const item of currentItems) {
        if (removedKeys.includes(item.id)) {
          currentItems = currentItems.filter(e => e.id !== item.id);
        }
      }
      if (addedItems.length) {
        addedItems.forEach(e => {
          if (!currentItemKeys.has(e.id)) {
            currentItems.push(e);
            hasModified = true;
          }
        });
      }
      if (removedItems.length > 0) {
        hasModified = true;
      }
      if (currentItems.length > 0) {
        this.selectedCodes = currentItems;
        if (this.cytMacroId) {
          this.$changedState.next(hasModified);
        } else {
          this.$changedState.next(false);
        }
      } else {
        this.selectedCodes = [];
        this.$changedState.next(false);
      }
    },
    hasSelectionType({ id }) {
      const selectedType = this.selectedCodes.findIndex(e => e.cytCodeGroupId === id);
      return selectedType > -1;
    },
    handleComment(item) {
      const selectedTypeName = this.typeMap[item.id];
      const selectedType = this.selectedCodes.findIndex(e => e.cytCodeGroupId === item.id);
      if (selectedType > -1) {
        const selectedCode = this.selectedCodes[selectedType];
        this.commentTargetId = item.id;
        this.commentLabel = selectedTypeName;
        this.comment = selectedCode?.comments || "";
      }
      //Save the scroll pos of the container ref;

      this.isCommenterOpen = true;
    },
    submitComment() {
      const targetId = this.commentTargetId;
      console.log(this.selectedCodes);
      const targetIdx = this.selectedCodes.findIndex(c => {
        return c.cytCodeGroupId === targetId;
      });
      if (targetIdx > -1) {
        const record = this.selectedCodes[targetIdx];
        this.selectedCodes = [
          ...this.selectedCodes.slice(0, targetIdx),
          { ...record, comments: this.comment },
          ...this.selectedCodes.slice(targetIdx + 1)
        ];
        this.saveCytologyResults();
      }
      this.comment = "";
      this.commentTargetId = null;
      this.isCommenterOpen = false;
    },
    modifierSelectionChanged(e) {
      const { addedItems, removedItems } = e;
      const removedKeys = removedItems.map(e => e.id);
      let currentItems = [...this.selectedModifiers];
      const currentItemKeys = new Set(currentItems.map(e => e.id));
      let hasModified = false;
      for (const item of currentItems) {
        if (removedKeys.includes(item.id)) {
          currentItems = currentItems.filter(e => e.id !== item.id);
        }
      }
      if (addedItems.length) {
        addedItems.forEach(e => {
          if (!currentItemKeys.has(e.id)) {
            currentItems.push(e);
            hasModified = true;
          }
        });
      }
      if (removedItems.length > 0) {
        hasModified = true;
      }
      if (currentItems.length > 0) {
        this.selectedModifiers = currentItems;
        if (this.cytMacroId) {
          this.$changedState.next(hasModified);
        } else {
          this.$changedState.next(false);
        }
      } else {
        this.selectedModifiers = [];
        this.$changedState.next(false);
      }
    },
    async saveCytologyResults(isModified) {
      try {
        this.isLoading = false;
        const diagCodeIds = [];
        const diagnosisIcds = [];
        //Iterate as it is rendered to ensure it saves with same order.

        const targetCodes = await Promise.all(
          this.selectedCodes.map(async e => {
            const details = await cytologyService.getSingleEntity(
              CytologyEntities.CytDiagnosticCodes,
              e.id
            );
            return {
              ...details,
              comments: e.comments
            };
          })
        );

        for (const targetCode of targetCodes) {
          diagnosisIcds.push(...targetCode.icdCodes);
          diagCodeIds.push({
            cytDiagnosticCodeId: targetCode.id,
            comments: targetCode.comments
          });
        }
        for (const targetCode of this.selectedModifiers) {
          diagCodeIds.push({
            cytDiagnosticCodeId: targetCode.id,
            comments: targetCode.comments
          });
        }
        // Generate the appropriate html content
        this.$emit("submit", {
          diagnosisIcds,
          cytDiagnosticMacroId: this.cytMacroId,
          macroIsModified: isModified,
          diagCodes: diagCodeIds
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async handleSelectMacro(id) {
      const macro = await cytologyService.getSingleEntity(CytologyEntities.CytDiagnosticMacros, id);
      if (macro) {
        const { codes } = macro;
        const codeDetails = await Promise.all(
          codes
            //Sort by sequence number
            .sort((a, b) => a.seqNum - b.seqNum)
            .map(e =>
              cytologyService
                .getSingleEntity(CytologyEntities.CytDiagnosticCodes, e.cytDiagnosticCodeId)
                .then(res => {
                  return {
                    ...res,
                    seqNum: e.seqNum
                  };
                })
            )
        );
        const diagCodes = codeDetails.sort((a, b) => a.seqNum - b.seqNum);
        const selectedCodeKeys = new Set();
        const selectedModifierKeys = new Set();
        for (const code of diagCodes) {
          const { cytCodeGroupId } = code;
          const isTopGroup = this.topCodes.includes(cytCodeGroupId);
          if (isTopGroup) {
            if (!selectedCodeKeys.has(code.cytDiagnosticCodeId) && selectedCodeKeys.size < 1) {
              selectedCodeKeys.add(code.id);
            }
          } else {
            if (!selectedModifierKeys.has(code.id) && selectedModifierKeys.size < 1) {
              selectedModifierKeys.add(code.id);
            }
          }
        }
        this.selectedCodes = diagCodes;
        this.$changedState.next(false);
      }
    },

    createCytTypeFilters(cytTypeId) {
      let filters = [];
      if (cytTypeId === CytTypeEnum.GynecologicalCytology) {
        // For gynecological cytology, include both Gynecological and Thin-Prep And Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum.GynecologicalCytology],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (cytTypeId === CytTypeEnum["Thin-Prep"]) {
        // For Thin-Prep, include both Thin-Prep and Thin-Prep And Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum["Thin-Prep"]],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]]
        ]);
      } else if (cytTypeId === CytTypeEnum["Thin Prep And Gynecological"]) {
        // For Thin-Prep And Gynecological, include all three types
        filters.push([
          ["cytTypeId", CytTypeEnum["Thin-Prep"]],
          "or",
          ["cytTypeId", CytTypeEnum.GynecologicalCytology],
          "or",
          ["cytTypeId", CytTypeEnum["Thin Prep And Gynecological"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (
        cytTypeId === CytTypeEnum["Non-Gynecological Cytology"] ||
        cytTypeId === CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]
      ) {
        // For Non-Gynecological Cytology, include both Hybrid and Non-Gynecological codes
        filters.push([
          ["cytTypeId", CytTypeEnum["Non-Gynecological Cytology"]],
          "or",
          ["cytTypeId", CytTypeEnum["Hybrid Gynecological/Non-Gynecological Cytology"]]
        ]);
      } else if (cytTypeId) {
        filters.push(["cytTypeId", "=", cytTypeId]);
      }
      return filters;
    }
  },
  watch: {
    cytMacroId(nv, ov) {
      if (nv && nv !== ov) {
        this.result.cytDiagnosticMacroId = nv;
        this.handleSelectMacro(nv);
      }
    },
    isCommenterOpen(nv) {
      const previewRef = this.$refs.preview;
      if (!nv) {
        this.$nextTick(() => {
          if (previewRef) {
            if (this.lastScrollPos) {
              scrollToPosition(previewRef, this.lastScrollPos);
            } else {
              scrollToElement(previewRef);
            }
          }
        });
      } else {
        // Save the scroll position of the container when the commenter is opened
        const scrollPos = getScrollPosition(previewRef);
        if (scrollPos) {
          this.lastScrollPos = scrollPos;
        }
      }
      this.$emit("toggleCommenter", nv);
    }
  },
  computed: {
    ...mapGetters(["cytReviewType"]),
    ...mapState({
      prefix: state => state.accessionStore.casePrefix,
      adequacyTerm: state => state.labSettings.CytologyTermAdequacy,
      commentTerm: state => state.labSettings.CytologyTermComment,
      diagnosisTerm: state => state.labSettings.CytologyTermDiagnosis,
      reccomendationTerm: state => state.labSettings.CytologyTermRecommendation,
      hormonalTerm: state => state.labSettings.CytologyTermHormonal,
      isSessionRescreen: state => state.sessionDetails.reScreen
    }),
    selectedCodeKeys: {
      get() {
        return this.selectedCodes.map(e => e.id);
      }
    },
    selectedModifierKeys: {
      get() {
        return this.selectedModifiers.map(e => e.id);
      }
    },
    cytologyCodes() {
      return cytologyService.createSearch(CytologyEntities.CytDiagnosticCodes);
    },
    hasModifiers() {
      if (!this.selectedTypeKey?.length) {
        return false;
      }
      const typeId = parseInt(this.selectedTypeKey[0]);
      return [CytCodeGroupEnum.Adequacy, CytCodeGroupEnum.Diagnosis].includes(typeId);
    },

    cytologyMacroSource() {
      const dataSource = new DataSource({
        store: cytologyService.createSearch(CytologyEntities.CytDiagnosticMacros),
        filter: dateRangeFilter("effectiveOn", "expiryOn"),
        pageSize: 5
      });
      return dataSource;
    },

    cytTypeId() {
      return this.prefix.cytTypeId;
    },
    typeMap() {
      return Object.keys(CytCodeGroupEnum).reduce((acc, key) => {
        acc[CytCodeGroupEnum[key]] = key;
        return acc;
      }, {});
    },
    topCodes() {
      return [
        CytCodeGroupEnum.Adequacy,
        CytCodeGroupEnum.Diagnosis,
        CytCodeGroupEnum.Comment,
        CytCodeGroupEnum.GeneralCategorization,
        CytCodeGroupEnum.Hormonal,
        CytCodeGroupEnum.Recommendation
      ];
    },
    selectedCodesAndMods() {
      if (!this.selectedCodes.length && !this.selectedModifiers.length) {
        return null;
      }
      return [...this.selectedCodes, ...this.selectedModifiers];
    },

    topTypes() {
      return this.mainTypes.map(e => e.id);
    },

    mainTypes() {
      return [
        {
          id: CytCodeGroupEnum.Adequacy,
          name: this.adequacyTerm || "Adequacy"
        },
        {
          id: CytCodeGroupEnum.Diagnosis,
          name: this.diagnosisTerm || "Diagnosis"
        },
        {
          id: CytCodeGroupEnum.Recommendation,
          name: this.reccomendationTerm || "Recommendation"
        },
        {
          id: CytCodeGroupEnum.Hormonal,
          name: this.hormonalTerm || "Hormonal"
        },
        {
          id: CytCodeGroupEnum.Comment,
          name: this.commentTerm || "Comment"
        }
      ];
    },
    codesSource() {
      if (!this.selectedTypeKey?.length) {
        return [];
      }
      const groupFilter = this.selectedTypeKey.map(e => ["cytCodeGroupId", e]);
      const filter = [
        groupFilter.reduce((acc, curr) => {
          if (acc.length) {
            acc.push("or");
          }
          acc.push(curr);
          return acc;
        }, [])
      ];
      const cytTypeFilter = this.createCytTypeFilters(this.cytTypeId);

      if (cytTypeFilter.length) {
        filter.push("and");
        filter.push(cytTypeFilter);
      }
      return new DataSource({
        key: "id",
        store: this.cytologyCodes,
        searchExpr: ["name", "code"],
        sort: [
          { selector: "cytCodeGroupId", desc: false },
          { selector: "severityLevel", desc: true }
        ],
        filter,
        postProcess(data) {
          return data.map(e => {
            return {
              ...e,
              text: e.name
            };
          });
        }
      });
    },

    modifiersSource() {
      if (!this.selectedTypeKey?.length) {
        return [];
      }
      const modifiers = this.selectedTypeKey.map(e => CytologyModifierMap[e]).filter(e => e);

      let cytTypeFilter = this.createCytTypeFilters(this.cytTypeId);

      const filter = [
        modifiers
          .map(code => ["cytCodeGroupId", "=", code])
          .reduce((acc, val) => {
            if (acc.length) {
              acc.push("or");
            }
            acc.push(val);
            return acc;
          }, [])
      ];
      if (cytTypeFilter.length) {
        filter.push("and");
        filter.push(cytTypeFilter);
      }

      return new DataSource({
        key: "id",
        store: this.cytologyCodes,
        searchExpr: ["name", "code"],
        sort: [
          { selector: "cytCodeGroupId", desc: false },
          { selector: "severityLevel", desc: true }
        ],
        filter,
        postProcess(data) {
          return data.map(e => {
            return {
              ...e,
              text: e.name
            };
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.builder-container {
  color: hsl(0, 0%, 10%);
}
::v-deep .diagnosis-code {
  padding: 0;
}
.name {
  font-size: 1.15rem;
  font-weight: bold;
}
.list {
  max-height: 300px;
  overflow-y: auto;
}

.list-item-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
}

.list-item-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: inline-block;
}
.btn-selected {
  color: #212529;
  background-color: #e2e6ea;
  border-color: #dae0e5;
}

dl {
  display: flex;
  color: hsl(0, 0%, 40%);
  column-gap: 0.5rem;
}

dt {
  font-weight: 400;
  margin-bottom: 0.25rem;
}
</style>
